import {
  CommandGroup,
  CommandItem,
  CommandList,
  CommandInput,
} from "./command"
import { Command as CommandPrimitive } from "cmdk"
import { useState, useRef, useCallback, type KeyboardEvent, useEffect } from "react"

import { Skeleton } from "./skeleton"

import { Check } from "lucide-react"
import { cn } from "@/lib/utils"
import { Input } from "components"

export type Option = Record<"value" | "label", string> & Record<string, string>

type AutoCompleteProps = {
  options: Option[]
  emptyMessage: string
  value?: Option
  onValueChange?: (value: Option) => void
  isLoading?: boolean
  disabled?: boolean
  placeholder?: string
  inputValue: string
  onInputChange: (value: string) => void
}

export const AutoComplete = ({
  options,
  placeholder,
  emptyMessage,
  value,
  onValueChange,
  disabled,
  isLoading = false,
  inputValue,
  onInputChange,
}: AutoCompleteProps) => {
  const inputRef = useRef<HTMLInputElement>(null)

  const [isOpen, setOpen] = useState(false)
  const [selected, setSelected] = useState<Option | undefined>(value)
  const [localInputValue, setLocalInputValue] = useState(inputValue || value?.label || "")
  
  // Update local state when props change
  useEffect(() => {
    if (value) {
      setSelected(value)
    }
  }, [value])

  // Update local input value when external input value or selected value changes
  useEffect(() => {
    // If a value is selected, prioritize showing its label
    if (selected) {
      setLocalInputValue(selected.label)
    } else {
      // Otherwise use the input value
      setLocalInputValue(inputValue)
    }
  }, [selected, inputValue])

  const handleKeyDown = useCallback(
    (event: KeyboardEvent<HTMLDivElement>) => {
      const input = inputRef.current
      if (!input) {
        return
      }

      // Keep the options displayed when the user is typing
      if (!isOpen) {
        setOpen(true)
      }

      // This is not a default behaviour of the <input /> field
      if (event.key === "Enter" && input.value !== "") {
        const optionToSelect = options.find(
          (option) => option.label === input.value,
        )
        if (optionToSelect) {
          setSelected(optionToSelect)
          onValueChange?.(optionToSelect)
        }
      }

      if (event.key === "Escape") {
        input.blur()
      }
    },
    [isOpen, options, onValueChange],
  )

  const handleBlur = useCallback(() => {
    setOpen(false)
    // Restore selected value's label on blur if there is a selection
    if (selected) {
      setLocalInputValue(selected.label)
    }
  }, [selected])

  const handleSelectOption = useCallback(
    (selectedOption: Option) => {
      setSelected(selectedOption)
      setLocalInputValue(selectedOption.label)
      onValueChange?.(selectedOption)

      // This is a hack to prevent the input from being focused after the user selects an option
      // We can call this hack: "The next tick"
      setTimeout(() => {
        inputRef?.current?.blur()
      }, 0)
    },
    [onValueChange],
  )

  const handleInputChange = useCallback(
    (value: string) => {
      setLocalInputValue(value)
      onInputChange(value)
      
      // Clear selection if input value changes manually
      if (selected && value !== selected.label) {
        setSelected(undefined)
      }
    },
    [onInputChange, selected],
  )

  return (
    <CommandPrimitive className="" onKeyDown={handleKeyDown}>
      <div>
        <Input
          ref={inputRef}
          value={localInputValue}
          onChange={isLoading ? undefined : handleInputChange}
          onBlur={handleBlur}
          onFocus={() => setOpen(true)}
          placeholder={placeholder}
          disabled={disabled}
          type="number"
          wrapClassName="border rounded-lg"
          className="text-base h-14 w-full border-gray-300 focus:border-blue-700 p-4"
        />
      </div>
      <div className="relative mt-1">
        <div
          className={cn(
            "animate-in fade-in-0 zoom-in-95 absolute top-0 z-10 w-full rounded-xl bg-white outline-none",
            isOpen ? "block" : "hidden",
          )}
        >
          <CommandList className="rounded-lg ring-1 ring-slate-200 bg-white-A700">
            {isLoading ? (
              <CommandPrimitive.Loading>
                <div className="p-1">
                  <Skeleton className="h-8 w-full" />
                </div>
              </CommandPrimitive.Loading>
            ) : null}
            {options?.length > 0 && !isLoading ? (
              <CommandGroup className="p-0">
                {options.map((option) => {
                  const isSelected = selected?.value === option.value
                  return (
                    <CommandItem
                      key={option.value}
                      value={option.label}
                      onMouseDown={(event) => {
                        event.preventDefault()
                        event.stopPropagation()
                      }}
                      onSelect={() => handleSelectOption(option)}
                      className={cn(
                        "flex w-full items-center font-medium hover:font-semibold hover:bg-blue-100 py-3 hover:text-blue-700 gap-1.5",
                        !isSelected ? "pl-3" : null,
                      )}
                    >
                      {isSelected ? <Check className="w-4" /> : null}
                      {option.label}
                    </CommandItem>
                  )
                })}
              </CommandGroup>
            ) : null}
            {!isLoading ? (
              <CommandPrimitive.Empty className="select-none rounded-sm px-2 py-3 text-center text-sm bg-white-A700">
                {emptyMessage}
              </CommandPrimitive.Empty>
            ) : null}
          </CommandList>
        </div>
      </div>
    </CommandPrimitive>
  )
}